import { Http } from 'lib'

import { mediaPageInitialState, MediaPageState } from './store/State'

import mediaPageReducer from './store/Reducer'

import {
  setFile,
  setError,
  reset,
  setIsUploading,
  MediaPageAction,
  setLinkUrl,
  setLinkName,
  setIsLinkUrlValid,
  setIsSavingLink
} from './store/Actions'

import ServiceProviderBase from 'lib/ServiceProviderBase'
import { MediaFileType, MediaFileExtension } from 'types/media-upload/IMediaTypes'
import { Link } from './Interfaces'
import apiUrlsAddresses from 'config/constants/api-urls-addresses'
import IMediaResponse from 'types/media-upload/IMediaResponse'
import { IMediaFileUploadPayload, IMediaLinkPayload } from 'types/media-upload/IMediaUpload'
const URL_REGEX = /^(https?|ftp|torrent|image|irc):\/\/(-\.)?([^\s\/?\.#-]+\.?)+(\/[^\s]*)?$/i

export class MediaPageService extends ServiceProviderBase<MediaPageState, MediaPageAction> {
  constructor() {
    super(mediaPageInitialState)
  }

  reduce(state: MediaPageState, action: MediaPageAction): MediaPageState {
    return mediaPageReducer(state, action)
  }

  reset() {
    this.dispatch(reset())
  }

  isValidType(file: Blob, allowedTypes: MediaFileType[], allowedExtensions: MediaFileExtension[]): boolean {
    const [type, extension] = file.type.split('/') as [MediaFileType, MediaFileExtension]
    return allowedTypes.includes(type) && allowedExtensions.includes(extension)
  }

  isValidSize(file: Blob, size: number): boolean {
    return file.size / 1024 <= size
  }

  setFile(file: File) {
    if (!file) {
      return
    }

    const isValidType = this.isValidType(file, ['application'] as MediaFileType[], ['pdf'] as MediaFileExtension[])
    const isValidSize = this.isValidSize(file, 5000)

    if (!isValidType) {
      this.dispatch(setError('This file type is now allowed.'))
      return
    }

    if (!isValidSize) {
      this.dispatch(setError('This file is too big.'))
      return
    }
    this.dispatch(setFile(file))
    this.dispatch(setError(null))
  }

  clearFile() {
    this.dispatch(setFile(null))
  }

  clearLink() {
    this.dispatch(setLinkName(''))
    this.dispatch(setLinkUrl(''))
    this.dispatch(setIsLinkUrlValid(true))
  }

  async doUploadFile(file: File): Promise<[null | IMediaResponse, null | string]> {
    this.dispatch(setIsUploading(true))
    const headers = {
      'Content-Type': 'multipart/form-data'
    }
    const formData = new FormData()
    formData.append('file', file)
    const [data, , error] = await Http.post<IMediaFileUploadPayload, IMediaResponse>(
      apiUrlsAddresses.me.media.file(),
      formData,
      null,
      headers
    )
    this.dispatch(setIsUploading(false))
    return [data, error]
  }

  onChangeLinkUrl(value: string) {
    this.dispatch(setLinkUrl(value))
    this.dispatch(setIsLinkUrlValid(true))
  }

  onChangeLinkName(value: string) {
    this.dispatch(setLinkName(value))
  }

  async doSave(link: Link): Promise<[null | IMediaResponse, null | string]> {
    const isValid = URL_REGEX.test(link.url)
    this.dispatch(setIsLinkUrlValid(isValid))
    if (!isValid) {
      return null
    }

    this.dispatch(setIsSavingLink(true))
    const [data, , error] = await Http.post<IMediaLinkPayload, IMediaResponse>(apiUrlsAddresses.me.media.link(), link)
    this.dispatch(setIsSavingLink(false))
    this.clearLink()
    return [data, error]
  }

  async doDeleteLink(index: number): Promise<[null | IMediaResponse, null | string]> {
    this.dispatch(setIsSavingLink(true))
    const [data, , error] = await Http.$delete<IMediaResponse>(apiUrlsAddresses.me.media.deleteLink(index))
    this.dispatch(setIsSavingLink(false))
    return [data, error]
  }
}

let instance: MediaPageService = null

export default (() => {
  if (!instance) {
    instance = new MediaPageService()
  }

  return instance
})()
